import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { InterviewerFilter as InterviewerFilterType } from '../../types/interviewer';
import { apiService } from '../../services/api';

interface InterviewerFilterProps {
  onFilterChange: (filter: InterviewerFilterType) => void;
  availableLanguages?: string[];
  availableSpecialties?: string[];
}

const initialFilter: InterviewerFilterType = {
  languages: [],
  specialty: [],
  availableFrom: undefined,
  availableTo: undefined,
};

export function InterviewerFilter({ onFilterChange, availableLanguages = [], availableSpecialties = [] }: InterviewerFilterProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<InterviewerFilterType>(initialFilter);
  const [languages, setLanguages] = useState<string[]>(availableLanguages);
  const [specialties, setSpecialties] = useState<string[]>(availableSpecialties);

  // Load available languages and specialties from API if not provided
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        if (availableLanguages.length === 0) {
          const langs = await apiService.interviewers.getAvailableLanguages();
          setLanguages(langs);
        }
        if (availableSpecialties.length === 0) {
          const specs = await apiService.interviewers.getAvailableSpecialties();
          setSpecialties(specs);
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };

    loadFilterOptions();
  }, [availableLanguages, availableSpecialties]);

  const updateFilter = (newFilter: InterviewerFilterType) => {
    setFilter(newFilter);
    onFilterChange(newFilter);
  };

  const handleLanguageToggle = (language: string) => {
    const newLanguages = filter.languages?.includes(language)
      ? filter.languages.filter(l => l !== language)
      : [...(filter.languages || []), language];
    
    updateFilter({ ...filter, languages: newLanguages });
  };

  const handleSpecialtyToggle = (specialty: string) => {
    const newSpecialties = filter.specialty?.includes(specialty)
      ? filter.specialty.filter(s => s !== specialty)
      : [...(filter.specialty || []), specialty];
    
    updateFilter({ ...filter, specialty: newSpecialties });
  };

  const handleDateChange = (type: 'availableFrom' | 'availableTo', value: string) => {
    updateFilter({ ...filter, [type]: value ? new Date(value) : undefined });
  };

  const handleReset = () => {
    setFilter(initialFilter);
    onFilterChange(initialFilter);
    
    // Reset form inputs - use a timeout to ensure DOM is updated
    setTimeout(() => {
      const dateInputs = document.querySelectorAll('input[type="date"]');
      dateInputs.forEach(input => (input as HTMLInputElement).value = '');
    }, 0);
  };

  const hasActiveFilters = 
    (filter.languages && filter.languages.length > 0) ||
    (filter.specialty && filter.specialty.length > 0) ||
    filter.availableFrom ||
    filter.availableTo;

  return (
    <div className="space-y-6">
      {/* Filter Header with Reset */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stitch-fg">
          {t('interviewers.filters', 'Filters')}
        </h3>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-sm font-medium text-stitch-primary transition-colors hover:text-stitch-primary-hover"
          >
            <span className="material-symbols-outlined text-base">close</span>
            {t('common.reset', 'Reset')}
          </button>
        )}
      </div>

      {/* Languages Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-stitch-fg-soft">
          {t('interviewers.languages', 'Languages')}
          {filter.languages && filter.languages.length > 0 && (
            <span className="ml-1 text-xs font-normal text-stitch-primary">
              ({filter.languages.length} selected)
            </span>
          )}
        </label>
        <div className="flex flex-wrap gap-2">
          {languages.map((language) => (
            <button
              key={language}
              onClick={() => handleLanguageToggle(language)}
              className={`rounded-stitch-lg border px-4 py-2 text-sm font-medium transition-all duration-stitch ease-stitch-out ${
                filter.languages?.includes(language)
                  ? 'border-stitch-primary bg-stitch-primary text-white shadow-stitch-card hover:bg-stitch-primary-hover'
                  : 'border-stitch-border bg-stitch-surface text-stitch-fg-soft hover:border-stitch-primary/40 hover:bg-stitch-primary-soft/50 hover:text-stitch-fg'
              }`}
            >
              {language}
            </button>
          ))}
        </div>
      </div>

      {/* Specialty Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-stitch-fg-soft">
          {t('interviewers.specialty', 'Specialty')}
          {filter.specialty && filter.specialty.length > 0 && (
            <span className="ml-1 text-xs font-normal text-stitch-accent">
              ({filter.specialty.length} selected)
            </span>
          )}
        </label>
        <div className="flex flex-wrap gap-2">
          {specialties.map((specialty) => (
            <button
              key={specialty}
              onClick={() => handleSpecialtyToggle(specialty)}
              className={`rounded-stitch-lg border px-4 py-2 text-sm font-medium transition-all duration-stitch ease-stitch-out ${
                filter.specialty?.includes(specialty)
                  ? 'border-stitch-accent bg-stitch-accent text-white shadow-stitch-card hover:bg-stitch-accent-hover'
                  : 'border-stitch-border bg-stitch-surface text-stitch-fg-soft hover:border-stitch-accent/40 hover:bg-stitch-accent-soft/40 hover:text-stitch-fg'
              }`}
            >
              {specialty}
            </button>
          ))}
        </div>
      </div>

      {/* Availability Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-stitch-fg-soft">
          {t('interviewers.availability', 'Availability')}
        </label>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-stitch-muted mb-1">
              {t('interviewers.from', 'From')}
            </label>
            <input
              type="date"
              onChange={(e) => handleDateChange('availableFrom', e.target.value)}
              className="w-full rounded-stitch-lg border border-stitch-border bg-stitch-surface px-3 py-2 text-sm transition-colors hover:border-stitch-primary/30 focus:border-stitch-primary focus:outline-none focus:ring-2 focus:ring-stitch-ring focus:ring-offset-2 focus:ring-offset-stitch-canvas"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stitch-muted mb-1">
              {t('interviewers.to', 'To')}
            </label>
            <input
              type="date"
              onChange={(e) => handleDateChange('availableTo', e.target.value)}
              className="w-full rounded-stitch-lg border border-stitch-border bg-stitch-surface px-3 py-2 text-sm transition-colors hover:border-stitch-primary/30 focus:border-stitch-primary focus:outline-none focus:ring-2 focus:ring-stitch-ring focus:ring-offset-2 focus:ring-offset-stitch-canvas"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="pt-4 border-t border-stitch-border">
          <div className="text-xs text-stitch-muted mb-2">Active filters:</div>
          <div className="flex flex-wrap gap-2">
            {filter.languages?.map((lang) => (
              <span
                key={lang}
                className="inline-flex items-center gap-1 rounded-stitch-md bg-stitch-primary-soft px-2 py-1 text-xs text-stitch-primary"
              >
                {lang}
                <button
                  onClick={() => handleLanguageToggle(lang)}
                  className="hover:text-stitch-primary-hover"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </span>
            ))}
            {filter.specialty?.map((spec) => (
              <span
                key={spec}
                className="inline-flex items-center gap-1 rounded-stitch-md bg-stitch-success-soft px-2 py-1 text-xs text-stitch-success dark:text-stitch-success"
              >
                {spec}
                <button
                  onClick={() => handleSpecialtyToggle(spec)}
                  className="hover:opacity-80"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 